import React, { useCallback, useMemo, useState } from 'react';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { useProfileSummaryStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import { useGetSingleProfile } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/useProfiles';
import { useAppSelector } from 'src/utils/Hooks';
import { ConfirmationDialog } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/components/ConfirmationDialog';
import { apiArchiveProfile } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/api/profileApis';
import { useSnackbar } from 'src/utils/Hooks';
import { useQueryClient } from '@tanstack/react-query';
import { BEACON_SUBTABS, BEACON_TABS, useBeaconRoutes } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';

const ArchiveProfileButton = () => {
  const { currentSubtab } = useBeaconRoutes();
  const { beaconNavigator } = useBeaconNavigator();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const viewMode = useProfileSummaryStore((s) => s.viewMode);
  const editingProfileId = useProfileSummaryStore((s) => s.editingProfileId);
  const { profile } = useGetSingleProfile(viewMode === 'edit' ? editingProfileId : undefined);
  const existingProfile: any = viewMode === 'edit' ? (profile as any) : undefined;

  const userId = useAppSelector((state) => state.user.session?.userId);

  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [isArchivingProfile, setIsArchivingProfile] = useState(false);

  // Only render on guidelineProfileSettings subtab
  const shouldRender = currentSubtab === BEACON_SUBTABS.PROFILES_SETTINGS;

  const canShowArchiveProfileButton = useMemo(() => {
    if (!shouldRender) return false;
    if (viewMode !== 'edit') return false;
    if (!existingProfile?.createdBy || !userId) return false;
    if (existingProfile?.status === 'ARCHIVED') return false;
    return String(existingProfile.createdBy) === String(userId);
  }, [shouldRender, viewMode, existingProfile?.createdBy, existingProfile?.status, userId]);

  const handleArchiveProfileConfirm = useCallback(async () => {
    if (viewMode !== 'edit' || !existingProfile?.profileId) return;
    try {
      setIsArchivingProfile(true);
      setIsArchiveDialogOpen(false);
      await apiArchiveProfile(existingProfile.profileId);
      showSnackbar({ message: 'Profile archived successfully.', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['Profiles'] });
      beaconNavigator.updateSearchParams({ tab: BEACON_TABS.PROFILES, subtab: BEACON_SUBTABS.PROFILES_ACTIVE });
      beaconNavigator.go();
    } catch {
      showSnackbar({ message: 'Failed to archive profile.', type: 'error' });
    } finally {
      setIsArchivingProfile(false);
    }
  }, [viewMode, existingProfile, showSnackbar, queryClient, beaconNavigator]);

  if (!canShowArchiveProfileButton) return null;

  return (
    <>
      <SlButton
        key="archiveProfileHeaderBtn"
        onClick={() => setIsArchiveDialogOpen(true)}
        disabled={isArchivingProfile}

        buttonSx={{ border: '1px solid #E0E4E7', backgroundColor: '#F6F7F8' }}
      >
        {isArchivingProfile ? 'Archiving...' : 'Archive'}
      </SlButton>
      <ConfirmationDialog
        open={isArchiveDialogOpen}
        onClose={() => setIsArchiveDialogOpen(false)}
        onConfirm={handleArchiveProfileConfirm}
        title="Archive Content Profile"
        message="Are you sure you want to archive this content profile? Once this profile is archived, it cannot be recovered."
        confirmButtonText="Archive"
        cancelButtonText="Cancel"
        isLoading={isArchivingProfile}
      />
    </>
  );
};

export default ArchiveProfileButton;


